import { useState } from "react";
import { colorRangesToWords } from "../lib/display/runtime.js";
import "./ComposeFields.css";
import "./RichTextDialog.css";

/**
 * Shared rich-text editor for headline/subline/CTA/sequence-frame text -
 * per-word color, glow, and line breaks.
 *
 * Rebuilt from an earlier contentEditable + document.execCommand
 * approach after that model proved persistently fragile in practice:
 * same-color selections silently no-op'd (browsers skip creating a span
 * when there's no effective style change), glow could be added but not
 * reliably removed, and selection state around swatch/checkbox clicks
 * was a recurring source of confusion. None of those failure modes are
 * possible here - there's no DOM range to lose track of, no selection to
 * go stale, no execCommand behavior to work around. Text editing (a
 * plain <textarea>) and per-word styling (a row of controls repeated
 * for every word, always visible - no click-to-select step) are
 * deliberately separate concerns rather than one surface trying to do
 * both at once.
 *
 * `words`: [{text, color, glow, breakAfter}] - each entry is fully
 * independent, no character-offset math anywhere. See runtime.js's
 * wordsToHtml/colorRangesToWords for the render/migration side.
 */

function wordsToPlainText(words) {
  let result = "";
  words.forEach((w, i) => {
    result += w.text;
    if (i < words.length - 1) result += w.breakAfter ? "\n" : " ";
  });
  return result;
}

/**
 * Re-derives the words array from the textarea's current text, matching
 * each word against the previous array by position + exact text to
 * decide whether to preserve its styling. A word that didn't change
 * keeps its color/glow/bold; a word that's new or edited starts
 * unstyled. Simple and predictable rather than attempting a fuzzier
 * diff/match.
 */
function deriveWordsFromText(text, previousWords) {
  const lines = (text ?? "").split("\n");
  const newWords = [];
  lines.forEach((line, lineIdx) => {
    const lineWords = line.split(/\s+/).filter(Boolean);
    lineWords.forEach((wordText, wordIdx) => {
      const prev = previousWords[newWords.length];
      const preserved = prev && prev.text === wordText;
      newWords.push({
        text: wordText,
        color: preserved ? prev.color : null,
        glow: preserved ? prev.glow : false,
        bold: preserved ? prev.bold : false,
        breakAfter: wordIdx === lineWords.length - 1 && lineIdx < lines.length - 1,
      });
    });
  });
  return newWords;
}

export default function RichTextDialog({
  title,
  initialText,
  initialColorRanges,
  initialWords,
  paletteColors,
  onSave,
  onClose,
}) {
  // Use the new words array directly if this layer/slot already has one
  // (already edited through this UI before); otherwise migrate, once,
  // from the older text+colorRanges format. Either way this only ever
  // runs on mount - lazy useState initializer, not an effect - so it
  // never re-runs and resets in-progress edits.
  const [words, setWords] = useState(() =>
    initialWords?.length ? initialWords : colorRangesToWords(initialText ?? "", initialColorRanges ?? [])
  );
  const [textValue, setTextValue] = useState(() => wordsToPlainText(words));

  function handleTextBlur() {
    setWords((prev) => deriveWordsFromText(textValue, prev));
  }

  function updateWord(index, patch) {
    setWords((ws) => ws.map((w, i) => (i === index ? { ...w, ...patch } : w)));
  }

  function handleSave() {
    // Text field may still hold an unblurred edit if the person clicks
    // Done directly from the textarea without tabbing/clicking away
    // first - re-derive one final time from its current value so that
    // edit isn't silently lost.
    const finalWords = deriveWordsFromText(textValue, words);
    onSave({ text: wordsToPlainText(finalWords), words: finalWords, colorRanges: [] });
  }

  return (
    <div className="rich-text-dialog__overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="rich-text-dialog">
        <div className="rich-text-dialog__header">
          <h2 className="rich-text-dialog__title">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close dialog" className="rich-text-dialog__close">
            ✕
          </button>
        </div>

        <textarea
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
          onBlur={handleTextBlur}
          rows={3}
          className="rich-text-dialog__editor"
        />

        {words.length > 0 ? (
          <>
            <span className="rich-text-dialog__swatches-label">Style each word</span>
            <div className="rich-text-dialog__word-list">
              {words.map((w, i) => (
                <div key={i} className="rich-text-dialog__word-row">
                  <span className="rich-text-dialog__word-row-label">
                    {w.color ? (
                      <span className="rich-text-dialog__word-row-dot" style={{ background: w.color }} />
                    ) : null}
                    {w.text}
                  </span>
                  <div className="rich-text-dialog__word-row-controls">
                    {(paletteColors ?? []).map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => updateWord(i, { color })}
                        style={{ background: color }}
                        aria-label={`Color "${w.text}" ${color}`}
                        className={`rich-text-dialog__swatch${w.color === color ? " rich-text-dialog__swatch--selected" : ""}`}
                      />
                    ))}
                    {w.color ? (
                      <button
                        type="button"
                        onClick={() => updateWord(i, { color: null, glow: false })}
                        aria-label={`Clear color for "${w.text}"`}
                        title="Clear color"
                        className="rich-text-dialog__swatch rich-text-dialog__swatch--clear"
                      >
                        ✕
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => updateWord(i, { bold: !w.bold })}
                      aria-pressed={!!w.bold}
                      aria-label={`Toggle bold for "${w.text}"`}
                      title="Bold"
                      className={`rich-text-dialog__icon-toggle${w.bold ? " rich-text-dialog__icon-toggle--active" : ""}`}
                    >
                      <span style={{ fontWeight: 700, fontSize: "0.75rem", lineHeight: 1 }}>B</span>
                    </button>
                    <button
                      type="button"
                      disabled={!w.color}
                      onClick={() => updateWord(i, { glow: !w.glow })}
                      aria-pressed={!!w.glow}
                      aria-label={`Toggle glow for "${w.text}"`}
                      title="Glow"
                      className={`rich-text-dialog__icon-toggle${w.glow ? " rich-text-dialog__icon-toggle--active" : ""}`}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 2l1.5 5.5L19 9l-5.5 1.5L12 16l-1.5-5.5L5 9l5.5-1.5L12 2z" />
                        <circle cx="19" cy="17" r="1.5" />
                        <circle cx="5" cy="18" r="1" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => updateWord(i, { breakAfter: !w.breakAfter })}
                      aria-pressed={!!w.breakAfter}
                      aria-label={`Toggle line break after "${w.text}"`}
                      title="Line break after"
                      className={`rich-text-dialog__icon-toggle${w.breakAfter ? " rich-text-dialog__icon-toggle--active" : ""}`}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
                        <path d="M9 10l-5 5 5 5" />
                        <path d="M4 15h12a4 4 0 0 0 4-4V4" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        <div className="rich-text-dialog__actions">
          <button type="button" onClick={onClose} className="rich-text-dialog__cancel-btn">
            Cancel
          </button>
          <button type="button" onClick={handleSave} className="rich-text-dialog__save-btn">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}